import { Search, User } from "lucide-react";

import { useState, useEffect } from "react";

const Pagination = ({ pagination, onPageChange }) => {
  const [inputPage, setInputPage] = useState(pagination?.page || 1);
  
  useEffect(() => {
    setInputPage(pagination?.page || 1);
  }, [pagination?.page]);

  if (!pagination || pagination.totalPages <= 1) return null;
  const { page, totalPages } = pagination;

  const handleInputSubmit = (e) => {
    if (e.key === "Enter" || e.type === "blur") {
      let newPage = parseInt(inputPage, 10);
      if (isNaN(newPage)) newPage = page;
      if (newPage < 1) newPage = 1;
      if (newPage > totalPages) newPage = totalPages;
      setInputPage(newPage);
      if (newPage !== page) {
        onPageChange(newPage);
      }
    }
  };

  return (
    <div className="flex justify-center items-center gap-4 py-4 border-t">
      <button 
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
        className="px-4 py-2 border rounded font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 transition-colors"
      >
        Previous
      </button>
      <span className="text-gray-600 font-medium flex items-center gap-2">
        Page 
        <input 
          type="number" 
          value={inputPage} 
          onChange={(e) => setInputPage(e.target.value)} 
          onKeyDown={(e) => e.key === "Enter" && handleInputSubmit(e)}
          onBlur={handleInputSubmit}
          className="border rounded px-2 py-1 w-16 text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
          min={1}
          max={totalPages}
        /> 
        of {totalPages}
      </span>
      <button 
        disabled={page === totalPages}
        onClick={() => onPageChange(page + 1)}
        className="px-4 py-2 border rounded font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 transition-colors"
      >
        Next
      </button>
    </div>
  );
};

export default function VisitorsCard({
  visitors,
  sort,
  setSort,
  setPage,
  pagination,
  searchQuery,
  setSearchQuery,
  loadingVisitorId,
  onVisitorSelect,
  formatDate,
  draftStartDate,
  draftEndDate,
  setDraftStartDate,
  setDraftEndDate,
  onApply,
  onReset,
  isInvalidDateRange,
  onSearch,
}) {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* HEADER */}
      <div className="p-6 border-b border-gray-200 flex flex-col gap-4">
        {/* Search */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by ID or UUID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearch()}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button 
            onClick={onSearch}
            className="bg-indigo-600 text-white px-4 py-2 rounded font-medium hover:bg-indigo-700 transition-colors"
          >
            Search
          </button>
        </div>

        {/* Filters & Sort */}
        <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-3 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">From:</span>
            <input
              type="date"
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.target.value)}
              className="border border-gray-300 rounded p-1.5 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">To:</span>
            <input
              type="date"
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.target.value)}
              className="border border-gray-300 rounded p-1.5 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onApply}
              disabled={isInvalidDateRange()}
              className={`px-3 py-1.5 rounded text-sm font-medium transition text-white ${
                isInvalidDateRange()
                  ? "bg-indigo-300 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              Apply
            </button>
            {(draftStartDate || draftEndDate) && (
              <button
                onClick={onReset}
                className="bg-red-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-red-600 transition"
              >
                Reset
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm font-medium text-gray-600">Sort by:</span>
            <select
              className="border border-gray-300 rounded p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={sort}
              onChange={(e) => {
                setPage(1);
                setSort(e.target.value);
              }}
            >
              <option value="latest">Latest</option>
              <option value="most_buy_clicks">Most Buy Clicks</option>
              <option value="most_actions">Most Actions</option>
              <option value="most_sessions">Most Sessions</option>
            </select>
          </div>
        </div>
      </div>

      {/* LIST */}
      <div>
        {visitors.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-semibold">No visitors found</p>
            <p className="text-sm mt-1">
              Try adjusting the date range or search criteria.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {visitors.map((v) => (
              <div
                key={v.visitor_id}
                onClick={() => onVisitorSelect(v.visitor_id)}
                className="p-6 hover:bg-gray-50 cursor-pointer transition"
              >
                {loadingVisitorId === v.visitor_id && (
                  <div className="text-sm text-indigo-600 font-semibold animate-pulse mb-2">
                    Loading journey…
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="bg-indigo-100 p-3 rounded-full mr-4">
                      <User className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-mono text-sm font-semibold">
                        ID: {v.static_id || "-"}
                      </p>
                      <p className="text-sm text-gray-500">
                        Last Active: {formatDate(v.last_activity)}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-6">
                    <Stat label="Sessions" value={v.session_count} />
                    <Stat label="Actions" value={v.action_count} />
                    <Stat label="Buy Clicks" value={v.buy_clicks} highlight />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Pagination pagination={pagination} onPageChange={setPage} />
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="text-center">
      <p className={`text-xl font-bold ${highlight ? "text-green-600" : ""}`}>
        {value}
      </p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}
